/**
 * Trilipiderm Skin Quiz — recommendation engine
 *
 * Pure logic, no DOM. Implements the Routine & Recommendation Master Spec v1:
 * a fixed spine plus four variable slots, resolved from eight answers.
 *
 * The engine never reads product data from itself — the section passes in a
 * catalog built from Shopify so prices, variants and availability stay live.
 * Products are referenced by slug; CATALOG_KEYS maps each slug to a handle
 * plus the variant selector used to pick the right size/scent.
 */
(function (global) {
  'use strict';

  /* ------------------------------------------------------------------ *
   * Product references — handle + variant selector (spec section 6)
   * ------------------------------------------------------------------ */

  var CATALOG_KEYS = {
    cleanser_ff: { handle: 'hydrating-exfoliant-face-cleanser', sku: '1124' },
    cleanser_bergamot: { handle: 'hydrating-exfoliant-face-cleanser', sku: '1123' },
    toner_rosehip: { handle: 'youthful-complexion-rosehip-toning-elixir', sku: '9223' },
    toner_niacinamide: { handle: 'radiant-renewal-niacinamide-toner', sku: '9224' },
    serum_vitc: { handle: 'triple-vitamin-c-brightening-serum', sku: '8101' },
    serum_dewysilk: { handle: 'dewysilk-serum-glass-skin-face-serum', sku: '8899' },
    treatment_retinol: { handle: 'advanced-retinol-3-phase-system', sku: '9200' },
    treatment_pore: { handle: 'plumping-line-and-pore-perfector-multi-corrective-serum', sku: '8001' },
    eye_creme: { handle: 'lifting-firming-eye-creme', sku: '8102' },
    neck_lift: { handle: 'agedefy-neck-lift', sku: '8120' },
    night_creme: { handle: 'rehydration-night-creme', sku: '7001' },
    day_creme_spf: { handle: 'protective-day-creme-spf30', sku: '9001' },
    // Spec says "SKU TBD"; the product exists in the catalogue as Natural / Coconut.
    lip_balm_natural: { handle: 'rosehip-nourish-repair-lip-treatment-hydrating-lip-care', sku: '9225' },
    lip_balm_coconut: { handle: 'rosehip-nourish-repair-lip-treatment-hydrating-lip-care', sku: '9226' },
    // Spec says 9221/9222; the live catalogue carries a single variant, 9313.
    lipid_e_lips: { handle: 'lipid-e-lips-spf30-hydrating-protective-lip-balm', sku: '9313' },
    // Body cleanser fragrance-free variants carry no SKU, so match on variant title.
    body_cleanser_ff: { handle: 'hydrating-exfoliant-body-cleanser', variant: 'Fragrance Free / 8 oz' },
    body_oil_ff: { handle: 'hydrating-all-body-oil', sku: '2000' },
    body_creme: { handle: 'all-body-moisture-retention-creme-hydrating-body-moisturizer', sku: '1001' },
    body_spf: { handle: 'all-body-moisture-retention-creme-spf30', sku: '6001' },
  };

  /* ------------------------------------------------------------------ *
   * Routine step numbers (spec sections 1 and 6)
   *
   * The 8-step face order is the spec's declared source of truth, so it is the
   * order products are listed and added to the cart in. Slots that hold two
   * products (Age Defense treatment and eye/neck) share a step number and keep
   * their insertion order, since Array#sort is stable.
   * ------------------------------------------------------------------ */

  var FACE_STEP = {
    cleanser_ff: 1,
    cleanser_bergamot: 1,
    toner_rosehip: 2,
    toner_niacinamide: 2,
    serum_vitc: 3,
    serum_dewysilk: 3,
    treatment_retinol: 4,
    treatment_pore: 4,
    eye_creme: 5,
    neck_lift: 5,
    night_creme: 6,
    // Spec calls this "6+7" — one product filling the AM moisturize and protect
    // steps (hard rule 3). It sorts as 7 and is still a single line item.
    day_creme_spf: 7,
    lip_balm_natural: 8,
    lip_balm_coconut: 8,
    lipid_e_lips: 8,
  };

  /* Body routine order (spec section 5): cleanse, treat & nourish, moisturize, protect. */
  var BODY_STEP = {
    body_cleanser_ff: 1,
    body_oil_ff: 2,
    body_creme: 3,
    body_spf: 4,
  };

  function orderBy(map) {
    return function (slugs) {
      return slugs.slice().sort(function (a, b) {
        return (map[a] || 99) - (map[b] || 99);
      });
    };
  }

  var byFaceStep = orderBy(FACE_STEP);
  var byBodyStep = orderBy(BODY_STEP);

  /* Scent options offered when the shopper said scented is fine (spec section 5). */
  var BODY_SCENTS = {
    cleanser: [
      { key: 'ff', label: 'Fragrance Free', variant: 'Fragrance Free / 8 oz' },
      { key: 'lavender', label: 'Lavender', variant: 'Lavender / 8 oz' },
      { key: 'lemongrass', label: 'Lemongrass', variant: 'Lemongrass / 8 oz' },
      { key: 'bergamot', label: 'Bergamot', variant: 'Bergamot / 8 oz' },
    ],
    oil: [
      { key: 'ff', label: 'Unscented', sku: '2000' },
      { key: 'bergamot', label: 'Bergamot', sku: '8021' },
      { key: 'lavender', label: 'Lavender', sku: '8026' },
      { key: 'lemongrass', label: 'Lemongrass', sku: '8024' },
      { key: 'teatree', label: 'Tea Tree', sku: '8022' },
    ],
  };

  /* ------------------------------------------------------------------ *
   * Copy building blocks
   *
   * Result copy is generated from the resolved routine, so a product can only
   * be mentioned if it is genuinely a line item. Labels live here next to the
   * product references they describe.
   * ------------------------------------------------------------------ */

  /* Plain product names — kept clause-free so they read well inside any list. */
  var PRODUCT_LABEL = {
    cleanser_ff: 'the fragrance-free Hydrating Exfoliant Cleanser',
    cleanser_bergamot: 'the Hydrating Exfoliant Cleanser',
    toner_rosehip: 'the Rosehip Toning Elixir',
    toner_niacinamide: 'the Radiant Renewal Niacinamide Toner',
    serum_vitc: 'Triple Vitamin C',
    serum_dewysilk: 'DewySilk Serum',
    treatment_retinol: 'the Advanced Retinol Treatment',
    treatment_pore: 'the Plumping Line & Pore Perfector',
    eye_creme: 'the Lifting & Firming Eye Creme',
    neck_lift: 'the AgeDefy Neck Lift',
    night_creme: 'Rehydration Night Creme',
    day_creme_spf: 'Protective Day Creme SPF 30',
    lip_balm_natural: 'the Rosehip lip treatment',
    lip_balm_coconut: 'the Rosehip lip treatment',
    lipid_e_lips: 'Lipid-E-Lips SPF 30',
  };

  /* One "why it's here" line, used only for the product actually in the slot. */
  var PRODUCT_WHY = {
    serum_vitc: 'Triple Vitamin C pairs vitamin C with Glutathione to brighten and even tone.',
    serum_dewysilk: 'DewySilk Serum carries the hydration, layering under your moisturizer.',
    toner_niacinamide: 'Niacinamide does the oil-balancing and pore-refining work.',
  };

  /* How the shopper described their skin, for the opening sentence. */
  var SKIN_FEEL = {
    dry: 'Your skin feels dry and tight',
    normal: 'Your skin feels fairly balanced',
    tzone: 'Your skin gets oily through the T-zone',
    oily: 'Your skin feels oily all over',
    reactive: 'Your skin is reactive and easily irritated',
  };

  /** "A", "A and B", "A, B and C" */
  function joinList(items) {
    if (items.length <= 1) return items[0] || '';
    if (items.length === 2) return items[0] + ' and ' + items[1];
    return items.slice(0, -1).join(', ') + ' and ' + items[items.length - 1];
  }

  /** Sentence-case, so a label like "the Eye Creme" can open a sentence. */
  function sentence(text) {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  /* ------------------------------------------------------------------ *
   * Scoring (spec section 4.2)
   * ------------------------------------------------------------------ */

  var BUCKETS = ['glow', 'hydrating', 'sensitive', 'problematic', 'age'];

  /* Deterministic — never random. Sensitive wins every tie. */
  var TIE_BREAK = ['sensitive', 'hydrating', 'problematic', 'age', 'glow'];

  var Q1_TO_BUCKET = {
    dry: 'hydrating',
    normal: null,
    tzone: 'problematic',
    oily: 'problematic',
    reactive: 'sensitive',
  };

  function score(answers) {
    var b = {};
    BUCKETS.forEach(function (k) {
      b[k] = 0;
    });

    if (answers.q3 && b.hasOwnProperty(answers.q3)) b[answers.q3] += 3;

    (answers.q4 || []).forEach(function (k) {
      if (b.hasOwnProperty(k)) b[k] += 1;
    });

    var mapped = Q1_TO_BUCKET[answers.q1];
    if (mapped) b[mapped] += 1;

    if (answers.q2 === '45plus') b.age += 1;

    return b;
  }

  /** Highest scorer, resolving ties through TIE_BREAK. `exclude` skips a bucket. */
  function topBucket(scores, exclude, minimum) {
    var best = null;
    TIE_BREAK.forEach(function (k) {
      if (k === exclude) return;
      if (scores[k] < (minimum || 0)) return;
      if (best === null || scores[k] > scores[best]) best = k;
    });
    return best;
  }

  function concerns(answers) {
    var scores = score(answers);
    var primary = topBucket(scores, null, 0);
    // A secondary only counts if it actually scored. Without this floor a
    // shopper who skips Q4 picks up a phantom secondary from the tie-break
    // order and loses their treatment step.
    var secondary = topBucket(scores, primary, 1);
    return { scores: scores, primary: primary, secondary: secondary };
  }

  /* ------------------------------------------------------------------ *
   * Slot table (spec sections 1 and 6) — never hand-written bundles
   * ------------------------------------------------------------------ */

  var ROUTINES = {
    glow: {
      name: 'Glow Routine',
      headline: 'Brighten, even, and glow.',
      goal: 'brighten dullness, even out tone, and fade dark spots',
      blurb: 'Brightening and radiance — for dullness, uneven tone, and dark spots.',
      toner: 'toner_rosehip',
      serum: 'serum_vitc',
      treatment: ['treatment_retinol'],
      optional: [],
    },
    hydrating: {
      name: 'Hydrating Routine',
      headline: 'Plump, dewy, and hydrated.',
      goal: 'restore deep hydration and relieve tightness',
      blurb: 'Deep moisture — for dry, dehydrated, tight, or flaky skin.',
      toner: 'toner_rosehip',
      serum: 'serum_dewysilk',
      treatment: [],
      optional: ['treatment_retinol'],
    },
    sensitive: {
      name: 'Sensitive Skin Routine',
      headline: 'Calm, comfortable, and barrier-strong.',
      goal: 'calm redness, reactivity, and irritation',
      blurb: 'Calm and barrier-first — for redness, reactivity, and easily irritated skin.',
      toner: 'toner_rosehip',
      serum: 'serum_dewysilk',
      treatment: [],
      optional: ['treatment_retinol'],
    },
    problematic: {
      name: 'Problematic / Combination Skin Routine',
      headline: 'Balanced, clarified, and refined.',
      goal: 'clarify congestion, refine visible pores, and balance oiliness',
      blurb: 'Clarifying and pore-refining — for congestion, oiliness in the T-zone, and visible pores.',
      toner: 'toner_niacinamide',
      serum: 'serum_vitc',
      treatment: [],
      optional: ['treatment_pore', 'treatment_retinol'],
    },
    age: {
      name: 'Age Defense Routine',
      headline: 'Lift, firm, and smooth.',
      goal: 'firm, smooth fine lines, and lift a crepey neck',
      blurb: 'Firming and line-smoothing — for loss of firmness, fine lines, and crepey neck.',
      toner: 'toner_niacinamide',
      serum: 'serum_vitc',
      treatment: ['treatment_retinol', 'treatment_pore'],
      optional: [],
    },
  };

  /* ------------------------------------------------------------------ *
   * Resolution
   * ------------------------------------------------------------------ */

  function resolve(answers) {
    var c = concerns(answers);
    var primary = c.primary;
    var routine = ROUTINES[primary];
    var isSensitive = primary === 'sensitive';

    var toner = routine.toner;
    var serum = routine.serum;
    var treatment = routine.treatment.slice();
    var notes = [];

    /* --- one-swap rule (spec 4.3): the secondary changes exactly one slot --- */
    var swap = null;
    if (c.secondary) {
      if (c.secondary === 'hydrating' && serum !== 'serum_dewysilk') {
        serum = 'serum_dewysilk';
        swap = 'serum';
      } else if (c.secondary === 'glow' && serum !== 'serum_vitc') {
        serum = 'serum_vitc';
        swap = 'serum';
      } else if (c.secondary === 'problematic' && toner !== 'toner_niacinamide') {
        toner = 'toner_niacinamide';
        swap = 'toner';
      } else if (c.secondary === 'sensitive') {
        // The one carve-out that changes two things — intentional, for safety.
        if (toner !== 'toner_rosehip') toner = 'toner_rosehip';
        treatment = [];
        swap = 'toner+treatment';
      }
      // secondary 'age' is handled by the age modifier instead — no swap.
    }

    /* --- age modifier (spec 4.4): only ever adds --- */
    var eyeNeck = [];
    var firmnessChosen = answers.q3 === 'age' || (answers.q4 || []).indexOf('age') !== -1;

    if (answers.q2 === '45plus') {
      eyeNeck = ['eye_creme', 'neck_lift'];
      if (treatment.indexOf('treatment_pore') === -1) treatment.push('treatment_pore');
    } else if (answers.q2 === '30to44') {
      eyeNeck = ['eye_creme'];
    } else if (firmnessChosen) {
      eyeNeck = ['eye_creme'];
    }

    /* --- hard rules 2 and 6: sensitive never carries a default treatment,
           and that overrides the age modifier --- */
    if (isSensitive) treatment = [];

    /* --- cleanser variant (hard rule 1) --- */
    var forceFragranceFree = isSensitive || answers.q5 === 'ff';
    var cleanser = forceFragranceFree ? 'cleanser_ff' : 'cleanser_bergamot';

    /* --- lip care (hard rule 10: the fragrance rule applies to flavors too) --- */
    var lip = forceFragranceFree ? 'lip_balm_natural' : 'lip_balm_coconut';

    /* --- tiers (spec section 2), listed in 8-step routine order --- */
    var core = byFaceStep([cleanser, toner, serum, 'night_creme', 'day_creme_spf']);
    var complete = byFaceStep(core.concat(treatment, eyeNeck, [lip]));

    // Age Defense always defaults to Complete regardless of Q7.
    var tier = primary === 'age' ? 'complete' : answers.q7 === 'full' ? 'complete' : 'core';

    /* --- add-ons: never default line items, excluded from tier price --- */
    var addons = ['lipid_e_lips'];
    routine.optional.forEach(function (slug) {
      if (complete.indexOf(slug) === -1 && addons.indexOf(slug) === -1) addons.push(slug);
    });

    /* --- copy, generated FROM the resolved routine ---
     * Every sentence is built by walking the products that are actually in the
     * routine, so copy cannot name something the shopper isn't getting. Add-on
     * guidance is kept separate, keyed by the add-on it belongs to.
     */
    var addonNotes = {};
    var lineItems = core.concat(treatment, eyeNeck, [lip]);
    var inRoutine = function (slug) {
      return lineItems.indexOf(slug) !== -1;
    };

    // Daily actives — driven by the slots the quiz actually decided. The
    // cleanser carries the exfoliation story, because the Main Line has no
    // standalone exfoliant for it to point at (spec section 8).
    var dailyParts = [];
    if (inRoutine(toner)) dailyParts.push(PRODUCT_LABEL[toner]);
    if (inRoutine(serum)) dailyParts.push(PRODUCT_LABEL[serum]);
    if (dailyParts.length) {
      notes.push(
        'Start with ' + PRODUCT_LABEL[cleanser] + ', which cleanses and gently exfoliates in one step, then ' +
          joinList(dailyParts) + ' ' + (dailyParts.length > 1 ? 'do' : 'does') + ' the daily work.'
      );
    }

    // One "why" line for the serum in the slot, and for a distinctive toner.
    if (inRoutine(serum) && PRODUCT_WHY[serum]) notes.push(PRODUCT_WHY[serum]);
    if (inRoutine(toner) && PRODUCT_WHY[toner]) notes.push(PRODUCT_WHY[toner]);

    // Evening treatments — only what is genuinely in the routine.
    var treatmentLabels = treatment.filter(inRoutine).map(function (s) {
      return PRODUCT_LABEL[s];
    });
    if (treatmentLabels.length) {
      notes.push('In the evening, ' + joinList(treatmentLabels) + ' ' + (treatmentLabels.length > 1 ? 'go' : 'goes') + ' on before moisturizer.');
      // Hard rule 4: the phase-up instruction rides with the 3 Phase System.
      if (inRoutine('treatment_retinol')) {
        notes.push('Start the retinol at phase 1 (1.5%) and work up from there.');
      }
    }

    var eyeNeckLabels = eyeNeck.filter(inRoutine).map(function (s) {
      return PRODUCT_LABEL[s];
    });
    if (eyeNeckLabels.length) {
      notes.push(
        sentence(joinList(eyeNeckLabels)) + ' ' + (eyeNeckLabels.length > 1 ? 'take care of' : 'takes care of') + ' the delicate areas, morning and night.'
      );
    }

    // Moisturize and protect — spine, so always present, but still checked.
    if (inRoutine('night_creme') && inRoutine('day_creme_spf')) {
      notes.push(
        'Finish with ' + PRODUCT_LABEL.night_creme + ' overnight and ' + PRODUCT_LABEL.day_creme_spf + ' every morning.'
      );
    }

    // Documented dry-skin exception (spec 3.2).
    if (primary === 'hydrating' && answers.q1 === 'dry' && inRoutine('night_creme')) {
      notes.push(sentence(PRODUCT_LABEL.night_creme) + ' can be used morning and night on very dry skin.');
    }

    // Q6 changes emphasis only, never products.
    if ((answers.q6 === 'no' || answers.q6 === 'sometimes') && inRoutine('day_creme_spf')) {
      notes.push('Daily SPF is the highest-impact step here, so make the morning creme a habit.');
    }

    // Optional upgrades carry their own guidance, never the routine copy.
    if (!inRoutine('treatment_retinol') && addons.indexOf('treatment_retinol') !== -1) {
      addonNotes.treatment_retinol = 'If you add this, start at phase 1 (1.5%) and work up from there.';
    }
    if (!inRoutine('treatment_pore') && addons.indexOf('treatment_pore') !== -1) {
      addonNotes.treatment_pore = 'For visible pores and uneven texture, layered in before your moisturizer.';
    }
    // Hard rule 9: positioned as daytime lip SPF, which the Rosehip balm has no
    // claim to. Adding it makes it the AM lip step and moves the balm to PM only.
    addonNotes.lipid_e_lips =
      'Daytime sun protection for your lips, which ' + PRODUCT_LABEL[lip] + ' does not provide. Add it and it becomes your morning lip step.';

    /* --- body module (spec section 5), driven by Q8 and nothing else --- */
    var body = null;
    if (answers.q8 && answers.q8 !== 'none') {
      var isSun = answers.q8 === 'sun';
      body = {
        lead: isSun ? 'sun' : 'dry',
        // Both answers return all four steps. The list stays in the spec's
        // application order so the step badges teach the right sequence; the
        // "leading with" products are called out in copy instead of being
        // hoisted above the cleanse step.
        items: byBodyStep(['body_cleanser_ff', 'body_oil_ff', 'body_creme', 'body_spf']),
        leadSlugs: isSun ? ['body_spf'] : ['body_creme', 'body_oil_ff'],
        leadNote: isSun
          ? 'Lead with the All-Body SPF 30 Creme — with your sun exposure, that is the step doing the most work.'
          : 'Lead with the Body Creme and All-Body Oil — those are the steps doing the most work on dry, rough skin.',
        // Fragrance-free is forced for any sensitive result; otherwise the
        // shopper picks the scent rather than us picking for them.
        scentLocked: forceFragranceFree,
        scents: forceFragranceFree ? null : BODY_SCENTS,
      };
    }

    return {
      primary: primary,
      secondary: c.secondary,
      scores: c.scores,
      swap: swap,
      routine: {
        key: primary,
        name: routine.name,
        headline: routine.headline,
        goal: routine.goal,
        blurb: routine.blurb,
      },
      slots: { cleanser: cleanser, toner: toner, serum: serum, treatment: treatment, eyeNeck: eyeNeck, lip: lip },
      core: core,
      complete: complete,
      tier: tier,
      addons: addons,
      addonNotes: addonNotes,
      body: body,
      notes: notes,
      // The two result paragraphs, generated here so the section renders text
      // rather than assembling it — keeps all copy under test.
      copy: {
        skinProblem:
          (SKIN_FEEL[answers.q1] || 'Your skin has its own needs') +
          ', and you want to ' +
          routine.goal +
          (c.secondary && ROUTINES[c.secondary]
            ? ', while also keeping an eye on how to ' + ROUTINES[c.secondary].goal
            : '') +
          '.',
        careRoutine: [routine.blurb.split('—')[0].trim() + '.'].concat(notes).join(' '),
      },
      // Caption under the AM/PM table. Only meaningful when retinol is actually
      // in the routine — Sensitive and other treatment-free routines get none.
      scheduleNote: treatment.indexOf('treatment_retinol') !== -1 ? 'Retinol is an evening step only.' : null,
    };
  }

  /**
   * AM / PM split for result-page display (spec section 6).
   *
   * Returned as rows rather than two flat lists, because the result table is
   * per-step and the two columns have to stay aligned. `options.lipidELipsAdded`
   * applies hard rule 9: once the shopper adds Lipid-E-Lips it becomes their AM
   * lip product and the Rosehip balm drops to PM only.
   */
  function schedule(result, options) {
    var opts = options || {};
    var slots = result.slots;
    return [
      { label: 'Cleanse', am: [slots.cleanser], pm: [slots.cleanser] },
      { label: 'Tone', am: [slots.toner], pm: [slots.toner] },
      { label: 'Serum', am: [slots.serum], pm: [slots.serum] },
      // Retinol is PM-only in every routine, so Treatment never has an AM cell.
      { label: 'Treatment', am: [], pm: slots.treatment.slice() },
      { label: 'Eye & neck', am: slots.eyeNeck.slice(), pm: slots.eyeNeck.slice() },
      { label: 'Moisturize', am: ['day_creme_spf'], pm: ['night_creme'] },
      { label: 'Lip', am: [opts.lipidELipsAdded ? 'lipid_e_lips' : slots.lip], pm: [slots.lip] },
    ];
  }

  var api = {
    CATALOG_KEYS: CATALOG_KEYS,
    BODY_SCENTS: BODY_SCENTS,
    ROUTINES: ROUTINES,
    BUCKETS: BUCKETS,
    TIE_BREAK: TIE_BREAK,
    FACE_STEP: FACE_STEP,
    BODY_STEP: BODY_STEP,
    score: score,
    concerns: concerns,
    resolve: resolve,
    schedule: schedule,
  };

  global.TrilipidermQuizEngine = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
